import { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Search,
  Edit2,
  X,
  Lightbulb,
  Diamond,
  Stethoscope,
  Wallet,
  Plus,
  ImagePlus,
  PencilLine,
  ChevronDown,
  ChevronRight,
  Trash2,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { Label } from "../../components/ui/label";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "../../components/ui/sheet";
import { useDoctors } from "../../hooks/useDoctors";
import { useDebounce } from "../../hooks/useDebounce";
import {
  DoctorDto,
  DoctorTitleOptionDto,
  DoctorsClient,
  UpsertDoctorProfileSettingCommand,
  CreateDoctorTitleCommand,
  UpdateDoctorTitleCommand,
  FileParameter,
  SpecialtiesClient,
  UpdateDoctorSpecialtyCommand,
  CreateSpecialtyCommand,
  SpecialtyDto,
} from "../../web-api-client";
import { DoctorImage } from "../../components/doctor/DoctorImage";
import Can from "../../auth/Can";
import { can, Perm } from "../../auth/permissions";
import { Skeleton } from "../../components/ui/skeleton";
import Alert from "../../components/ui/Alert";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { appConfig } from "../../appconfig";
import { fetchWithTimeout } from "../../utils/fetchWithTimeout";
import { makeFetchWithAuth } from "../../auth/authFetch";
import { useNavigate } from "react-router-dom";
import type { KeyboardEvent } from "react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../components/ui/collapsible";

function SectionTitle({
  icon,
  title,
}: {
  icon: React.ReactNode;
  title: string;
}) {
  return (
    <div className="flex items-center gap-2 text-slate-900">
      {icon}
      <h3 className="text-sm font-semibold">{title}</h3>
    </div>
  );
}

interface WorkExperienceBlock {
  id: string;
  label: string;
  items: string[];
}

function ExperienceBlockEditor({
  block,
  onUpdate,
  onRemove,
}: {
  block: WorkExperienceBlock;
  onUpdate: (block: WorkExperienceBlock) => void;
  onRemove: () => void;
}) {
  const [label, setLabel] = useState(block.label);
  const [items, setItems] = useState(block.items);
  const [isOpen, setIsOpen] = useState(true);

  const handleAddItem = () => {
    const newItems = [...items, ""];
    setItems(newItems);
    onUpdate({ ...block, items: newItems });
    if (!isOpen) setIsOpen(true);
  };

  const handleRemoveItem = (index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
    onUpdate({ ...block, items: newItems });
  };

  const handleItemChange = (index: number, value: string) => {
    const newItems = [...items];
    newItems[index] = value;
    setItems(newItems);
    onUpdate({ ...block, items: newItems });
  };

  const handleLabelChange = (value: string) => {
    setLabel(value);
    onUpdate({ ...block, label: value });
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleAddItem();
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="relative w-full flex items-center justify-between pr-4 pl-6 px-4 py-3 bg-white hover:bg-slate-50 transition-colors border-b border-slate-200"
          >
            <div className="absolute left-0 top-0 h-full bg-blue-500 w-[6px]" />
            <Input
              value={label}
              onChange={(e) => handleLabelChange(e.target.value)}
              placeholder="Nhập tiêu đề khối (VD: Quá trình đào tạo)"
              className="pointer-events-auto h-10 max-w-[420px] rounded-md border-slate-300 bg-white font-semibold"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove();
                }}
                className="h-8 w-8 text-slate-400 hover:text-red-600 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
              {isOpen ? (
                <ChevronDown className="w-4 h-4 text-slate-500" />
              ) : (
                <ChevronRight className="w-4 h-4 text-slate-500" />
              )}
            </div>
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent className="px-4 py-4 bg-slate-50/50">
          <div className="space-y-3 relative pl-6">
            <div className="absolute left-[9px] top-1 bottom-1 w-px bg-slate-200" />

            {items.map((item, index) => (
              <div key={index} className="relative">
                <div className="absolute -left-[18.3px] top-4 h-2 w-2 rounded-full bg-emerald-700" />
                <div className="flex gap-2 items-start">
                  <Textarea
                    value={item}
                    onChange={(e) => handleItemChange(index, e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={`Nhập nội dung mục ${index + 1}...`}
                    className="min-h-[56px] rounded-md border-slate-300 bg-white text-sm resize-none"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveItem(index)}
                    className="h-9 w-9 shrink-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={handleAddItem}
            className="mt-4 w-full border-dashed border-slate-300 bg-white text-blue-600 hover:bg-blue-50"
          >
            <Plus className="w-4 h-4 mr-2" />
            Thêm mục mới
          </Button>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

type EditFormState = {
  doctorId?: string;
  doctorTitleId?: number;
  specialtyId?: number;
  specialtyName?: string;
  summary?: string;
  image?: string;
  description?: WorkExperienceBlock[];
};

type SpecialtyOption = {
  id: number;
  name: string;
};

type TitleFormState = {
  id?: number;
  code: string;
  name: string;
  fee: string;
  sortOrder: string;
  isActive: boolean;
};

export default function DoctorManager() {
  const PAGE_SIZE = 30;
  const [pageNumber, setPageNumber] = useState(1);
  const navigate = useNavigate();

  const api = useMemo(() => {
    return new DoctorsClient(appConfig.apiBaseUrl, {
      fetch: makeFetchWithAuth(fetchWithTimeout(), {
        onUnauthorized: () =>
          navigate("/login", {
            replace: true,
            state: { from: window.location.pathname },
          }),
      }),
    });
  }, []);

  const apiSpecialties = useMemo(() => {
    const baseFetch = makeFetchWithAuth(fetchWithTimeout(), {
      onUnauthorized: () =>
        navigate("/login", {
          replace: true,
          state: { from: window.location.pathname },
        }),
    });

    return new SpecialtiesClient(appConfig.apiBaseUrl, {
      fetch: baseFetch,
    });
  }, [navigate]);

  const canDoctorManage = can(Perm.Doctor_Manage);

  const [specialtyOptions, setSpecialtyOptions] = useState<SpecialtyDto[]>([]);
  const [isLoadingSpecialties, setIsLoadingSpecialties] = useState(false);
  const [isSavingSpecialty, setIsSavingSpecialty] = useState(false);
  const [isDeletingSpecialtyId, setIsDeletingSpecialtyId] = useState<
    number | null
  >(null);

  const [isSpecialtyDialogOpen, setIsSpecialtyDialogOpen] = useState(false);
  const [specialtyFormError, setSpecialtyFormError] = useState("");
  const [specialtyName, setSpecialtyName] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDoctor, setSelectedDoctor] = useState<DoctorDto | null>(null);
  const [editFormState, setEditFormState] = useState<EditFormState>({});

  const [titleOptions, setTitleOptions] = useState<DoctorTitleOptionDto[]>([]);
  const [isLoadingTitles, setIsLoadingTitles] = useState(false);

  const [isSaving, setIsSaving] = useState(false);
  const [isDeletingImage, setIsDeletingImage] = useState(false);

  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  const [pendingImagePreview, setPendingImagePreview] = useState<string | null>(
    null,
  );

  const [submitError, setSubmitError] = useState<string>("");
  const [submitSuccess, setSubmitSuccess] = useState<string>("");

  const [isTitleDialogOpen, setIsTitleDialogOpen] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const [titleFormError, setTitleFormError] = useState("");

  const [isEditSpecialtyDialogOpen, setIsEditSpecialtyDialogOpen] =
    useState(false);
  const [isSavingEditSpecialty, setIsSavingEditSpecialty] = useState(false);
  const [editingSpecialty, setEditingSpecialty] =
    useState<SpecialtyOption | null>(null);
  const [editSpecialtyFormError, setEditSpecialtyFormError] = useState("");
  const [editSpecialtyForm, setEditSpecialtyForm] = useState({
    name: "",
    code: "",
    description: "",
  });

  const [titleForm, setTitleForm] = useState<TitleFormState>({
    code: "",
    name: "",
    fee: "",
    sortOrder: "",
    isActive: true,
  });

  const dSearch = useDebounce(searchTerm, 250);

  useEffect(() => {
    setPageNumber(1);
  }, [dSearch]);

  const {
    doctors,
    totalCount,
    hasNextPage,
    pageSize,
    isFetching,
    avatars,
    avatarErrors,
    refetch: refetchDoctors,
  } = useDoctors({
    date: "",
    specialty: null,
    availableOnly: false,
    searchTerm: dSearch,
    pageNumber,
    pageSize: PAGE_SIZE,
  });

  const totalPages = Math.max(1, Math.ceil((totalCount ?? 0) / pageSize));

  const openEditSpecialtyDialog = (item: SpecialtyOption) => {
    setEditingSpecialty(item);
    setEditSpecialtyFormError("");
    setEditSpecialtyForm({
      name: item.name ?? "",
      code: item.code ?? "",
      description: item.description ?? "",
    });
    setIsEditSpecialtyDialogOpen(true);
  };

  const loadSpecialties = async (preferredId?: number) => {
    setIsLoadingSpecialties(true);
    try {
      const items = await apiSpecialties.getSpeciaties();
      setSpecialtyOptions(items ?? []);

      if (preferredId) {
        const found = (items ?? []).find((x) => x.id === preferredId);
        if (found) {
          setEditFormState((prev) => ({
            ...prev,
            specialtyId: found.id,
            specialtyName: found.name,
          }));
        }
      }
    } catch (error: any) {
      setSubmitError(error?.message || "Không thể tải danh mục chuyên khoa.");
    } finally {
      setIsLoadingSpecialties(false);
    }
  };

  const loadTitleOptions = async (preferredId?: number) => {
    setIsLoadingTitles(true);
    try {
      const data = await api.getDoctorTitles();
      const items = data ?? [];
      setTitleOptions(items);

      if (preferredId) {
        setEditFormState((prev) => ({
          ...prev,
          doctorTitleId: preferredId,
        }));
      }
    } catch (error: any) {
      setSubmitError(
        error?.message || "Không thể tải danh mục chức danh / học vị.",
      );
    } finally {
      setIsLoadingTitles(false);
    }
  };

  useEffect(() => {
    loadTitleOptions();
    loadSpecialties();
  }, []);

  useEffect(() => {
    return () => {
      if (pendingImagePreview?.startsWith("blob:")) {
        URL.revokeObjectURL(pendingImagePreview);
      }
    };
  }, [pendingImagePreview]);

  const selectedTitle = useMemo(() => {
    if (!editFormState.doctorTitleId) return null;
    return (
      titleOptions.find((x) => x.id === editFormState.doctorTitleId) ?? null
    );
  }, [editFormState.doctorTitleId, titleOptions]);

  const getDoctorDisplayImage = (doctor: DoctorDto | null | undefined) => {
    if (!doctor) return undefined;
    return (
      (doctor as any).customImageUrl ||
      doctor.processedUrl ||
      doctor.originalUrl ||
      undefined
    );
  };

  const handleEditDoctor = (doctor: DoctorDto) => {
    setSubmitError("");
    setSubmitSuccess("");
    setPendingImageFile(null);

    if (pendingImagePreview?.startsWith("blob:")) {
      URL.revokeObjectURL(pendingImagePreview);
    }
    setPendingImagePreview(null);

    setSelectedDoctor(doctor);
    setEditFormState({
      doctorId: doctor.doctorId,
      doctorTitleId: doctor.doctorTitleId ?? undefined,
      specialtyName: doctor.specialty ?? "",
      summary: doctor.summary ?? "",
      image: getDoctorDisplayImage(doctor),
      specialtyId: (doctor as any).specialtyId ?? undefined,
    });
  };

  const handleSelectSpecialty = async (value: string) => {
    if (!selectedDoctor?.doctorId) return;

    const specialtyId = value ? Number(value) : undefined;
    const selected = specialtyOptions.find((x) => x.id === specialtyId);

    setEditFormState((prev) => ({
      ...prev,
      specialtyId,
      specialtyName: selected?.name ?? "",
    }));

    setIsSavingSpecialty(true);
    setSubmitError("");
    setSubmitSuccess("");

    try {
      const payload = new UpdateDoctorSpecialtyCommand();
      payload.doctorId = selectedDoctor.doctorId;
      payload.specialtyId = specialtyId;

      await apiSpecialties.updateDoctorSpecialty(
        selectedDoctor.doctorId,
        payload,
      );

      setSelectedDoctor((prev) =>
        prev
          ? ({
              ...prev,
              specialty: selected?.name ?? undefined,
            } as any)
          : prev,
      );

      await refetchDoctors();
      setSubmitSuccess("Đã cập nhật chuyên khoa bác sĩ.");
    } catch (error: any) {
      setSubmitError(error?.message || "Không thể cập nhật chuyên khoa.");
    } finally {
      setIsSavingSpecialty(false);
    }
  };

  const handleSaveEditSpecialty = async () => {
    if (!editingSpecialty?.id) return;

    if (!editSpecialtyForm.name.trim()) {
      setEditSpecialtyFormError("Tên chuyên khoa không được để trống.");
      return;
    }

    setEditSpecialtyFormError("");
    setIsSavingEditSpecialty(true);

    try {
      const payload = {
        id: editingSpecialty.id,
        name: editSpecialtyForm.name.trim(),
        code: editSpecialtyForm.code.trim() || undefined,
        description: editSpecialtyForm.description.trim() || undefined,
        isActive: true,
        displayOrder: editingSpecialty.displayOrder ?? 0,
      };

      await apiSpecialties.updateSpecialty(editingSpecialty.id, payload as any);

      const wasSelected = editFormState.specialtyId === editingSpecialty.id;

      await loadSpecialties(editingSpecialty.id);
      await refetchDoctors();

      if (wasSelected) {
        setEditFormState((prev) => ({
          ...prev,
          specialtyName: editSpecialtyForm.name.trim(),
        }));

        setSelectedDoctor((prev) =>
          prev
            ? ({
                ...prev,
                specialty: editSpecialtyForm.name.trim(),
              } as any)
            : prev,
        );
      }

      setIsEditSpecialtyDialogOpen(false);
      setSubmitSuccess("Đã cập nhật chuyên khoa.");
    } catch (error: any) {
      setEditSpecialtyFormError(
        error?.message || "Không thể cập nhật chuyên khoa.",
      );
    } finally {
      setIsSavingEditSpecialty(false);
    }
  };

  const handleAddExperienceBlock = () => {
    const newBlock: WorkExperienceBlock = {
      id: `exp-${Date.now()}`,
      label: "",
      items: [""],
    };

    const blocks = (editFormState.description as WorkExperienceBlock[]) || [];
    setEditFormState((prev) => ({
      ...prev,
      description: [...blocks, newBlock],
    }));
  };

  const handleUpdateBlock = (updatedBlock: WorkExperienceBlock) => {
    const blocks = (editFormState.description as WorkExperienceBlock[]) || [];
    setEditFormState((prev) => ({
      ...prev,
      description: blocks.map((b) =>
        b.id === updatedBlock.id ? updatedBlock : b,
      ),
    }));
  };

  const handleRemoveBlock = (blockId: string) => {
    const blocks = (editFormState.description as WorkExperienceBlock[]) || [];
    setEditFormState((prev) => ({
      ...prev,
      description: blocks.filter((b) => b.id !== blockId),
    }));
  };

  const openCreateSpecialtyDialog = () => {
    setSpecialtyFormError("");
    setSpecialtyName("");
    setIsSpecialtyDialogOpen(true);
  };

  const handleCreateSpecialty = async () => {
    if (!specialtyName.trim()) {
      setSpecialtyFormError("Tên chuyên khoa không được để trống.");
      return;
    }

    setSpecialtyFormError("");
    setIsSavingSpecialty(true);

    try {
      const payload = new CreateSpecialtyCommand();
      payload.name = specialtyName.trim();

      const newId = await apiSpecialties.createSpecialty(payload);

      await loadSpecialties(newId);

      if (selectedDoctor?.doctorId) {
        const updatePayload = new UpdateDoctorSpecialtyCommand();
        updatePayload.doctorId = selectedDoctor.doctorId;
        updatePayload.specialtyId = newId;

        await apiSpecialties.updateDoctorSpecialty(
          selectedDoctor.doctorId,
          updatePayload,
        );

        setEditFormState((prev) => ({
          ...prev,
          specialtyId: newId,
          specialtyName: specialtyName.trim(),
        }));

        setSelectedDoctor((prev) =>
          prev
            ? ({
                ...prev,
                specialty: specialtyName.trim(),
              } as any)
            : prev,
        );
      }

      await refetchDoctors();
      setIsSpecialtyDialogOpen(false);
      setSubmitSuccess("Đã thêm chuyên khoa mới.");
    } catch (error: any) {
      setSpecialtyFormError(error?.message || "Không thể thêm chuyên khoa.");
    } finally {
      setIsSavingSpecialty(false);
    }
  };

  const handleCloseDrawer = () => {
    if (pendingImagePreview?.startsWith("blob:")) {
      URL.revokeObjectURL(pendingImagePreview);
    }

    setPendingImageFile(null);
    setPendingImagePreview(null);
    setSelectedDoctor(null);
    setEditFormState({});
    setSubmitError("");
    setSubmitSuccess("");
  };

  const handleSave = async () => {
    if (!selectedDoctor?.doctorId) return;

    setSubmitError("");
    setSubmitSuccess("");
    setIsSaving(true);

    try {
      let uploadedImageUrl: string | undefined =
        getDoctorDisplayImage(selectedDoctor);

      if (pendingImageFile) {
        const file: FileParameter = {
          data: pendingImageFile,
          fileName: pendingImageFile.name,
        };

        uploadedImageUrl = await api.postApiDoctorsImage(
          selectedDoctor.doctorId,
          file,
        );

        if (pendingImagePreview?.startsWith("blob:")) {
          URL.revokeObjectURL(pendingImagePreview);
        }

        setPendingImageFile(null);
        setPendingImagePreview(null);
      }

      const payload = new UpsertDoctorProfileSettingCommand();
      payload.doctorId = selectedDoctor.doctorId;
      payload.doctorTitleId = editFormState.doctorTitleId ?? undefined;
      payload.summary = editFormState.summary?.trim() || undefined;
      payload.isActive = true;

      await api.upsertDoctorProfileSetting(payload);

      const nextTitle =
        titleOptions.find((x) => x.id === editFormState.doctorTitleId) ?? null;

      setSelectedDoctor((prev) =>
        prev
          ? ({
              ...prev,
              doctorTitleId: editFormState.doctorTitleId,
              title: nextTitle?.name ?? prev.title,
              consultationFee: nextTitle?.fee ?? prev.consultationFee,
              summary: editFormState.summary ?? "",
              customImageUrl: uploadedImageUrl,
              hasCustomImage: !!uploadedImageUrl,
            } as any)
          : prev,
      );

      setEditFormState((prev) => ({
        ...prev,
        image: uploadedImageUrl,
      }));

      await refetchDoctors();

      setSubmitSuccess("Đã lưu cấu hình bác sĩ.");
    } catch (error: any) {
      setSubmitError(error?.message || "Lưu thông tin bác sĩ thất bại.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteUploadedDoctorImage = async () => {
    if (!selectedDoctor?.doctorId) return;

    setSubmitError("");
    setSubmitSuccess("");
    setIsDeletingImage(true);

    try {
      await api.deleteDoctorImage(selectedDoctor.doctorId);

      if (pendingImagePreview?.startsWith("blob:")) {
        URL.revokeObjectURL(pendingImagePreview);
      }

      setPendingImageFile(null);
      setPendingImagePreview(null);

      const fallbackImage =
        selectedDoctor.processedUrl || selectedDoctor.originalUrl || undefined;

      setEditFormState((prev) => ({
        ...prev,
        image: fallbackImage,
      }));

      setSelectedDoctor((prev) =>
        prev
          ? ({
              ...prev,
              customImageUrl: undefined,
              hasCustomImage: false,
            } as any)
          : prev,
      );

      refetchDoctors();

      setSubmitSuccess("Đã xóa ảnh bác sĩ.");
    } catch (error: any) {
      setSubmitError(error?.message || "Không thể xóa ảnh bác sĩ.");
    } finally {
      setIsDeletingImage(false);
    }
  };

  const handleSelectDoctorImage = (file: File) => {
    if (file.size > 5 * 1024 * 1024) {
      setSubmitError("Ảnh tối đa 5MB.");
      return;
    }

    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setSubmitError("Chỉ hỗ trợ JPG, PNG, WEBP.");
      return;
    }

    setSubmitError("");
    setSubmitSuccess("");

    if (pendingImagePreview?.startsWith("blob:")) {
      URL.revokeObjectURL(pendingImagePreview);
    }

    const previewUrl = URL.createObjectURL(file);
    setPendingImageFile(file);
    setPendingImagePreview(previewUrl);

    setEditFormState((prev) => ({
      ...prev,
      image: previewUrl,
    }));
  };

  const handleClearImage = async () => {
    if (pendingImageFile) {
      if (pendingImagePreview?.startsWith("blob:")) {
        URL.revokeObjectURL(pendingImagePreview);
      }

      setPendingImageFile(null);
      setPendingImagePreview(null);

      setEditFormState((prev) => ({
        ...prev,
        image: getDoctorDisplayImage(selectedDoctor),
      }));
      return;
    }

    if ((selectedDoctor as DoctorDto)?.hasCustomImage) {
      const confirmed = window.confirm(
        "Bạn có chắc muốn xóa ảnh bác sĩ này không?",
      );
      if (!confirmed) return;

      await handleDeleteUploadedDoctorImage();
    }
  };

  const openCreateTitleDialog = () => {
    setTitleFormError("");
    setIsEditingTitle(false);
    setTitleForm({
      code: "",
      name: "",
      fee: "",
      sortOrder: "",
      isActive: true,
    });
    setIsTitleDialogOpen(true);
  };

  const openEditTitleDialog = () => {
    const item = titleOptions.find((x) => x.id === editFormState.doctorTitleId);
    if (!item) return;

    setTitleFormError("");
    setIsEditingTitle(true);
    setTitleForm({
      id: item.id,
      code: item.code ?? "",
      name: item.name ?? "",
      fee: String(item.fee ?? 0),
      sortOrder: String((item as any).sortOrder ?? 0),
      isActive: (item as any).isActive ?? true,
    });
    setIsTitleDialogOpen(true);
  };

  const handleSaveTitle = async () => {
    setTitleFormError("");

    if (!titleForm.code.trim()) {
      setTitleFormError("Mã chức danh / học vị không được để trống.");
      return;
    }

    if (!titleForm.name.trim()) {
      setTitleFormError("Tên chức danh / học vị không được để trống.");
      return;
    }

    if (Number.isNaN(Number(titleForm.fee || "0"))) {
      setTitleFormError("Phí khám không hợp lệ.");
      return;
    }

    if (Number.isNaN(Number(titleForm.sortOrder || "0"))) {
      setTitleFormError("Thứ tự sắp xếp không hợp lệ.");
      return;
    }

    setIsSavingTitle(true);

    try {
      if (isEditingTitle && titleForm.id) {
        const payload = new UpdateDoctorTitleCommand();
        payload.id = titleForm.id;
        payload.code = titleForm.code.trim();
        payload.name = titleForm.name.trim();
        payload.fee = Number(titleForm.fee || 0);
        payload.sortOrder = Number(titleForm.sortOrder || 0);
        payload.isActive = titleForm.isActive;

        await api.updateDoctorTitle(titleForm.id, payload);
        await loadTitleOptions(titleForm.id);
      } else {
        const payload = new CreateDoctorTitleCommand();
        payload.code = titleForm.code.trim();
        payload.name = titleForm.name.trim();
        payload.fee = Number(titleForm.fee || 0);
        payload.sortOrder = Number(titleForm.sortOrder || 0);
        payload.isActive = titleForm.isActive;

        const newId = await api.createDoctorTitle(payload);
        await loadTitleOptions(newId);
      }

      await refetchDoctors();
      setIsTitleDialogOpen(false);
    } catch (error: any) {
      setTitleFormError(error?.message || "Không thể lưu chức danh / học vị.");
    } finally {
      setIsSavingTitle(false);
    }
  };

  const showClearImageButton =
    canDoctorManage &&
    !!(pendingImageFile || (selectedDoctor as DoctorDto)?.hasCustomImage);

  const handleClearDoctorSpecialty = async () => {
    if (!selectedDoctor?.doctorId) return;

    setIsSavingSpecialty(true);
    setSubmitError("");
    setSubmitSuccess("");

    try {
      const payload = new UpdateDoctorSpecialtyCommand();
      payload.doctorId = selectedDoctor.doctorId;
      payload.specialtyId = undefined;

      await apiSpecialties.updateDoctorSpecialty(
        selectedDoctor.doctorId,
        payload,
      );

      setEditFormState((prev) => ({
        ...prev,
        specialtyId: undefined,
        specialtyName: "",
      }));

      setSelectedDoctor((prev) =>
        prev
          ? ({
              ...prev,
              specialty: undefined,
              specialtyId: undefined,
            } as any)
          : prev,
      );

      await refetchDoctors();
      setSubmitSuccess("Đã bỏ chuyên khoa của bác sĩ.");
    } catch (error: any) {
      setSubmitError(error?.message || "Không thể bỏ chuyên khoa của bác sĩ.");
    } finally {
      setIsSavingSpecialty(false);
    }
  };

  const handleDeleteSpecialty = async (item: SpecialtyDto) => {
    const confirmed = window.confirm(
      `Ngừng sử dụng chuyên khoa "${item.name}"?`,
    );
    if (!confirmed) return;

    setIsDeletingSpecialtyId(item.id!);
    setSubmitError("");
    setSubmitSuccess("");

    try {
      await apiSpecialties.deleteSpecialty(item.id!);

      const isCurrent = editFormState.specialtyId === item.id;

      if (isCurrent) {
        setEditFormState((prev) => ({
          ...prev,
          specialtyId: undefined,
          specialtyName: "",
        }));

        setSelectedDoctor((prev) =>
          prev
            ? ({
                ...prev,
                specialty: undefined,
                specialtyId: undefined,
              } as any)
            : prev,
        );
      }

      await loadSpecialties();
      await refetchDoctors();
      setSubmitSuccess("Đã ngừng sử dụng chuyên khoa.");
    } catch (error: any) {
      setSubmitError(error?.message || "Không thể ngừng sử dụng chuyên khoa.");
    } finally {
      setIsDeletingSpecialtyId(null);
    }
  };

  return (
    <div>
      <div>
        <div className="border-b-2 border-green-600 p-4 bg-white">
          <h2 className="text-2xl font-bold tracking-tight text-slate-900">
            Danh sách bác sĩ
          </h2>
        </div>

        <div className="p-6 flex flex-col gap-4">
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Tìm kiếm bác sĩ theo tên, chuyên khoa, chức danh..."
                className="pl-9 bg-slate-50 border-slate-200"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <Table>
              <TableHeader className="bg-slate-50/50">
                <TableRow>
                  <TableHead className="min-w-[220px]">Tên bác sĩ</TableHead>
                  <TableHead>Chuyên khoa</TableHead>
                  <TableHead>Chức danh</TableHead>
                  <TableHead>Phí khám</TableHead>
                  <Can perm={Perm.Doctor_Manage}>
                    <TableHead className="w-[120px] text-center">
                      Hành động
                    </TableHead>
                  </Can>
                </TableRow>
              </TableHeader>

              <TableBody>
                {isFetching ? (
                  Array.from({ length: 4 }).map((_, rowIndex) => (
                    <TableRow key={`skeleton-row-${rowIndex}`}>
                      {Array.from({ length: 5 }).map((_, colIndex) => (
                        <TableCell
                          key={`skeleton-cell-${rowIndex}-${colIndex}`}
                        >
                          <Skeleton className="h-10 w-full" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : doctors.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="text-center text-slate-400 py-8"
                    >
                      Không tìm thấy bác sĩ phù hợp.
                    </TableCell>
                  </TableRow>
                ) : (
                  doctors.map((doctor) => (
                    <TableRow
                      key={doctor.doctorId}
                      className={cn(
                        "hover:bg-slate-50/50 cursor-pointer transition-colors",
                        selectedDoctor?.doctorId === doctor.doctorId &&
                          "bg-blue-50 hover:bg-blue-100",
                      )}
                      onClick={() => handleEditDoctor(doctor)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 overflow-hidden">
                            <DoctorImage
                              doctor={doctor}
                              hasAvatar={
                                avatars?.[String(doctor.doctorId ?? "")]
                              }
                              avatarError={
                                avatarErrors[String(doctor.doctorId)]
                              }
                            />
                          </div>
                          <span className="font-medium text-slate-900">
                            {doctor.name}
                          </span>
                        </div>
                      </TableCell>

                      <TableCell>
                        {doctor.specialty ? (
                          <Badge
                            variant="outline"
                            className="bg-slate-50 text-slate-700"
                          >
                            {doctor.specialty}
                          </Badge>
                        ) : (
                          <span className="text-slate-400 text-sm">—</span>
                        )}
                      </TableCell>

                      <TableCell className="text-slate-600">
                        {doctor.title || (
                          <span className="text-slate-400">—</span>
                        )}
                      </TableCell>

                      <TableCell className="text-slate-700">
                        {doctor.consultationFee ? (
                          <span className="font-medium text-emerald-700">
                            {doctor.consultationFee.toLocaleString("vi-VN")}đ
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </TableCell>

                      <Can perm={Perm.Doctor_Manage}>
                        <TableCell className="text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditDoctor(doctor);
                            }}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </Can>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          <div className="flex flex-col gap-3 border-t border-slate-200 px-4 py-4 md:flex-row md:items-center md:justify-between">
            <div className="text-sm text-slate-500">
              Hiển thị{" "}
              <span className="font-semibold text-slate-800">
                {doctors.length}
              </span>
              {" / "}
              {totalCount} bác sĩ
            </div>

            <div className="flex items-center justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPageNumber((prev) => Math.max(1, prev - 1))}
                disabled={pageNumber <= 1 || isFetching}
              >
                Trước
              </Button>

              <div className="px-3 text-sm text-slate-700">
                Trang <span className="font-semibold">{pageNumber}</span> /{" "}
                {totalPages}
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setPageNumber((prev) => prev + 1)}
                disabled={!hasNextPage || isFetching}
              >
                Sau
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Sheet
        open={selectedDoctor != null}
        onOpenChange={(open) => {
          if (!open) handleCloseDrawer();
        }}
      >
        <SheetContent className="w-full sm:max-w-2xl px-0 pt-0 bg-white">
          <div className="relative h-full">
            <div className="h-full overflow-y-auto pb-[88px]">
              <div className="px-6 pt-6 pb-6">
                <SheetHeader className="mb-6 border-b border-slate-200 pb-4">
                  <SheetTitle className="text-[20px] font-bold text-slate-900 text-left">
                    Chỉnh sửa thông tin bác sĩ
                  </SheetTitle>
                </SheetHeader>

                {!canDoctorManage && (
                  <Alert
                    variant="destructive"
                    msg="Bạn không có quyền thay đổi thông tin bác sĩ"
                    className="mb-4"
                  />
                )}

                {!!submitError && (
                  <Alert
                    variant="destructive"
                    msg={submitError}
                    className="mb-4"
                  />
                )}

                {!!submitSuccess && (
                  <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                    {submitSuccess}
                  </div>
                )}

                {selectedDoctor && (
                  <div className="space-y-6">
                    <SectionTitle
                      icon={<Diamond className="w-4 h-4 text-slate-500" />}
                      title="Thông tin cơ bản"
                    />

                    <div className="mt-5 flex flex-col items-center gap-5 sm:flex-row sm:items-start">
                      <div className="flex flex-col items-center gap-2 shrink-0">
                        <div className="relative group">
                          <div className="w-28 h-28 rounded-xl overflow-hidden border-2 border-slate-200 bg-[#dff1f5] ring-4 ring-slate-100">
                            {editFormState.image ? (
                              <img
                                src={editFormState.image}
                                alt={selectedDoctor.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <DoctorImage
                                doctor={selectedDoctor}
                                hasAvatar={
                                  avatars?.[
                                    String(selectedDoctor.doctorId ?? "")
                                  ]
                                }
                                avatarError={
                                  avatarErrors[String(selectedDoctor.doctorId)]
                                }
                              />
                            )}
                          </div>

                          <label
                            htmlFor="avatar-upload"
                            className={cn(
                              "absolute inset-0 flex items-center justify-center rounded-xl transition-colors cursor-pointer",
                              canDoctorManage
                                ? "bg-black/0 group-hover:bg-black/40"
                                : "pointer-events-none bg-transparent",
                            )}
                          >
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center gap-1 text-white">
                              <ImagePlus className="w-5 h-5" />
                              <span className="text-[11px] font-medium">
                                {pendingImageFile ? "Đã chọn ảnh" : "Đổi ảnh"}
                              </span>
                            </div>
                          </label>

                          <input
                            id="avatar-upload"
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            className="hidden"
                            disabled={
                              !canDoctorManage || isSaving || isDeletingImage
                            }
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                handleSelectDoctorImage(file);
                              }
                              e.target.value = "";
                            }}
                          />

                          {showClearImageButton && (
                            <button
                              type="button"
                              onClick={handleClearImage}
                              disabled={isSaving || isDeletingImage}
                              className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center shadow-md hover:bg-red-600 transition-colors disabled:opacity-50"
                              title={
                                pendingImageFile
                                  ? "Hủy ảnh đang chọn"
                                  : "Xóa ảnh đã upload"
                              }
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>

                        <p className="text-[11px] text-slate-400 text-center">
                          JPG, PNG, WEBP. Tối đa 5MB.
                        </p>
                      </div>

                      <div className="flex-1 w-full space-y-4">
                        <p className="text-[16px] font-semibold text-emerald-700">
                          {selectedDoctor.name}
                        </p>

                        <div className="grid grid-cols-1 gap-4">
                          <div className="space-y-1.5">
                            <Label className="text-sm font-medium text-slate-600">
                              Chuyên khoa
                            </Label>

                            <div className="flex gap-2">
                              <div className="flex-1">
                                <Select
                                  value={
                                    editFormState.specialtyId
                                      ? String(editFormState.specialtyId)
                                      : ""
                                  }
                                  onValueChange={handleSelectSpecialty}
                                  disabled={
                                    !canDoctorManage ||
                                    isLoadingSpecialties ||
                                    isSavingSpecialty
                                  }
                                >
                                  <SelectTrigger className="h-10 border-slate-300 bg-white focus:ring-emerald-500">
                                    <SelectValue
                                      placeholder={
                                        isLoadingSpecialties
                                          ? "Đang tải chuyên khoa..."
                                          : "Chọn chuyên khoa"
                                      }
                                    />
                                  </SelectTrigger>

                                  <SelectContent className="p-2">
                                    <button
                                      type="button"
                                      className="mb-2 flex w-full items-center justify-center gap-2 rounded-md border border-dashed border-emerald-300 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-100"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        openCreateSpecialtyDialog();
                                      }}
                                    >
                                      <Plus className="h-4 w-4" />
                                      Thêm chuyên khoa mới
                                    </button>

                                    {specialtyOptions.length === 0 ? (
                                      <div className="px-3 py-2 text-sm text-slate-400">
                                        Chưa có chuyên khoa
                                      </div>
                                    ) : (
                                      specialtyOptions.map((item) => (
                                        <div
                                          key={item.id}
                                          className="flex items-center gap-2 rounded-sm px-1 py-1 hover:bg-slate-50"
                                        >
                                          <SelectItem
                                            value={String(item.id)}
                                            className="flex-1"
                                          >
                                            {item.name}
                                          </SelectItem>

                                          <button
                                            type="button"
                                            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-400 hover:bg-blue-50 hover:text-blue-600"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              openEditSpecialtyDialog(item);
                                            }}
                                            title="Sửa chuyên khoa"
                                          >
                                            <PencilLine className="h-4 w-4" />
                                          </button>

                                          <button
                                            type="button"
                                            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                                            disabled={
                                              isDeletingSpecialtyId === item.id
                                            }
                                            onClick={async (e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              await handleDeleteSpecialty(item);
                                            }}
                                            title="Ngừng sử dụng chuyên khoa"
                                          >
                                            <X className="h-4 w-4" />
                                          </button>
                                        </div>
                                      ))
                                    )}
                                  </SelectContent>
                                </Select>
                              </div>

                              <Can perm={Perm.Doctor_Manage}>
                                <Button
                                  type="button"
                                  variant="outline"
                                  disabled={
                                    !editFormState.specialtyId ||
                                    isSavingSpecialty
                                  }
                                  onClick={handleClearDoctorSpecialty}
                                  title="Bỏ chuyên khoa của bác sĩ"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </Can>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1.5 md:col-span-2">
                        <Label className="text-sm font-medium text-slate-600">
                          Chức danh / học vị
                        </Label>

                        <div className="flex gap-2">
                          <div className="flex-1">
                            <Select
                              value={
                                editFormState.doctorTitleId
                                  ? String(editFormState.doctorTitleId)
                                  : ""
                              }
                              onValueChange={(val) =>
                                setEditFormState((prev) => ({
                                  ...prev,
                                  doctorTitleId: val ? Number(val) : undefined,
                                }))
                              }
                              disabled={!canDoctorManage || isLoadingTitles}
                            >
                              <SelectTrigger className="h-10 border-slate-300 bg-white focus:ring-emerald-500">
                                <SelectValue
                                  placeholder={
                                    isLoadingTitles
                                      ? "Đang tải danh mục..."
                                      : "Chọn chức danh / học vị"
                                  }
                                />
                              </SelectTrigger>
                              <SelectContent>
                                {titleOptions.map((item) => (
                                  <SelectItem
                                    key={item.id}
                                    value={String(item.id)}
                                  >
                                    {item.name} -{" "}
                                    {(item.fee ?? 1600000).toLocaleString(
                                      "vi-VN",
                                    )}
                                    đ
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <Can perm={Perm.Doctor_Manage}>
                            <Button
                              type="button"
                              variant="outline"
                              onClick={openCreateTitleDialog}
                              title="Thêm chức danh / học vị"
                            >
                              <Plus className="w-4 h-4" />
                            </Button>

                            <Button
                              type="button"
                              variant="outline"
                              onClick={openEditTitleDialog}
                              disabled={!editFormState.doctorTitleId}
                              title="Sửa chức danh / học vị đang chọn"
                            >
                              <PencilLine className="w-4 h-4" />
                            </Button>
                          </Can>
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-sm font-medium text-slate-600">
                          Phí khám
                        </Label>
                        <div className="h-10 rounded-md border border-slate-200 bg-emerald-50 px-3 flex items-center text-sm font-semibold text-emerald-700">
                          {selectedTitle
                            ? `${(selectedTitle.fee ?? 1600000).toLocaleString("vi-VN")}đ`
                            : "—"}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label
                        htmlFor="summary"
                        className="text-sm font-medium text-slate-600"
                      >
                        Giới thiệu
                      </Label>
                      <Textarea
                        id="summary"
                        value={editFormState.summary || ""}
                        onChange={(e) =>
                          setEditFormState((prev) => ({
                            ...prev,
                            summary: e.target.value,
                          }))
                        }
                        disabled={!canDoctorManage}
                        placeholder="Nhập mô tả ngắn hiển thị cho bác sĩ..."
                        className="min-h-[120px] border-slate-300 bg-white resize-y"
                      />
                    </div>

                    <div className="mt-3 mb-4 flex items-start gap-2 rounded-lg bg-blue-50 border border-blue-100 px-3 py-2.5 text-[13px] text-slate-600">
                      <Lightbulb className="w-4 h-4 text-blue-400 shrink-0 mt-px" />
                      <div>
                        <span>
                          Tạo các khối thông tin với tiêu đề khác nhau (Kinh
                          nghiệm, Quá trình đào tạo, Thành tựu nổi bật...).
                        </span>
                        <span className="ml-1 text-slate-400">
                          <br />
                          Ctrl + Enter: Tạo mục mới
                        </span>
                      </div>
                    </div>

                    <div className="space-y-3">
                      {(
                        editFormState.description as
                          | WorkExperienceBlock[]
                          | undefined
                      )?.map((block) => (
                        <ExperienceBlockEditor
                          key={block.id}
                          block={block}
                          onUpdate={handleUpdateBlock}
                          onRemove={() => handleRemoveBlock(block.id)}
                        />
                      ))}

                      <button
                        type="button"
                        onClick={handleAddExperienceBlock}
                        disabled={!canDoctorManage}
                        className="w-full rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-3.5 text-center cursor-pointer hover:border-emerald-400 hover:bg-emerald-50 transition-colors group disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 group-hover:text-emerald-600 transition-colors">
                          <Plus className="w-4 h-4" />
                          Thêm khối thông tin mới
                        </span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <SheetFooter className="absolute bottom-0 left-0 right-0 flex flex-row justify-end gap-3 border-t border-slate-200 bg-white px-6 py-4 shadow-[0_-4px_16px_rgba(15,23,42,0.04)]">
              <SheetClose asChild>
                <Button variant="outline" className="min-w-[110px]">
                  Hủy bỏ
                </Button>
              </SheetClose>

              <Can perm={Perm.Doctor_Manage}>
                <Button
                  onClick={handleSave}
                  disabled={isSaving || isDeletingImage}
                  className="min-w-[140px] bg-primary hover:bg-primary/90 text-white"
                >
                  {isSaving ? "Đang lưu..." : "Lưu thay đổi"}
                </Button>
              </Can>
            </SheetFooter>
          </div>
        </SheetContent>
      </Sheet>

      <Dialog open={isTitleDialogOpen} onOpenChange={setIsTitleDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {isEditingTitle
                ? "Cập nhật chức danh / học vị"
                : "Thêm chức danh / học vị"}
            </DialogTitle>
          </DialogHeader>

          {!!titleFormError && (
            <Alert variant="destructive" msg={titleFormError} />
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-600">Mã</Label>
              <Input
                value={titleForm.code}
                onChange={(e) =>
                  setTitleForm((prev) => ({
                    ...prev,
                    code: e.target.value,
                  }))
                }
                placeholder="VD: BSCKI"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-600">Tên</Label>
              <Input
                value={titleForm.name}
                onChange={(e) =>
                  setTitleForm((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
                placeholder="VD: Bác sĩ chuyên khoa I"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-600">
                Phí khám
              </Label>
              <Input
                value={titleForm.fee}
                onChange={(e) =>
                  setTitleForm((prev) => ({
                    ...prev,
                    fee: e.target.value,
                  }))
                }
                placeholder="1600000"
                inputMode="numeric"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-600">
                Thứ tự
              </Label>
              <Input
                value={titleForm.sortOrder}
                onChange={(e) =>
                  setTitleForm((prev) => ({
                    ...prev,
                    sortOrder: e.target.value,
                  }))
                }
                placeholder="0"
                inputMode="numeric"
              />
            </div>

            <div className="space-y-1.5 md:col-span-2">
              <Label className="text-sm font-medium text-slate-600">
                Trạng thái
              </Label>
              <Select
                value={titleForm.isActive ? "true" : "false"}
                onValueChange={(val) =>
                  setTitleForm((prev) => ({
                    ...prev,
                    isActive: val === "true",
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Chọn trạng thái" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Đang sử dụng</SelectItem>
                  <SelectItem value="false">Ngừng sử dụng</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsTitleDialogOpen(false)}
            >
              Hủy
            </Button>
            <Button onClick={handleSaveTitle} disabled={isSavingTitle}>
              {isSavingTitle ? "Đang lưu..." : "Lưu"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isSpecialtyDialogOpen}
        onOpenChange={setIsSpecialtyDialogOpen}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Thêm chuyên khoa</DialogTitle>
          </DialogHeader>

          {!!specialtyFormError && (
            <Alert variant="destructive" msg={specialtyFormError} />
          )}

          <div className="space-y-2 py-2">
            <Label className="text-sm font-medium text-slate-600">
              Tên chuyên khoa
            </Label>
            <Input
              value={specialtyName}
              onChange={(e) => setSpecialtyName(e.target.value)}
              placeholder="VD: Tim mạch"
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsSpecialtyDialogOpen(false)}
            >
              Hủy
            </Button>
            <Button
              onClick={handleCreateSpecialty}
              disabled={isSavingSpecialty}
            >
              {isSavingSpecialty ? "Đang lưu..." : "Thêm mới"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isEditSpecialtyDialogOpen}
        onOpenChange={setIsEditSpecialtyDialogOpen}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Cập nhật chuyên khoa</DialogTitle>
          </DialogHeader>

          {!!editSpecialtyFormError && (
            <Alert variant="destructive" msg={editSpecialtyFormError} />
          )}

          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-600">
                Tên chuyên khoa
              </Label>
              <Input
                value={editSpecialtyForm.name}
                onChange={(e) =>
                  setEditSpecialtyForm((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
                placeholder="VD: Nhi khoa"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsEditSpecialtyDialogOpen(false)}
            >
              Hủy
            </Button>
            <Button
              onClick={handleSaveEditSpecialty}
              disabled={isSavingEditSpecialty}
            >
              {isSavingEditSpecialty ? "Đang lưu..." : "Lưu"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
